import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../../components/ui/button";
import { PlusCircle, Filter, Search, ChevronDown, Ticket as TicketIcon } from "lucide-react";
import { Ticket } from "../../components/service-desk/TicketCard";
import { TicketListView } from "../../components/service-desk/TicketListView";
import { TicketFilterBar } from "../../components/service-desk/TicketFilterBar";
import { cn } from "../../components/ui/utils";
import { serviceDeskApi } from "../../utils/service-desk-api";
import { toast } from "sonner";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

const STATUS_CONFIG = {
  NEW: { label: "New Request", color: "bg-blue-50 text-blue-700 border-blue-100", kanbanColor: "bg-gray-50" },
  TODO: { label: "To Do", color: "bg-gray-100 text-gray-700 border-gray-200", kanbanColor: "bg-blue-50/50 border-blue-100" },
  DOING: { label: "Doing", color: "bg-orange-50 text-orange-700 border-orange-200", kanbanColor: "bg-orange-50/50 border-orange-100" },
  BLOCKED: { label: "Blocked", color: "bg-red-50 text-red-700 border-red-200", kanbanColor: "bg-red-50/50 border-red-100" },
  RESOLVED: { label: "Resolved", color: "bg-green-50 text-green-700 border-green-200", kanbanColor: "bg-green-50/50 border-green-100" },
};

const PRIORITY_CONFIG = {
  URGENT: "text-red-600 bg-red-50 border-red-200",
  HIGH: "text-orange-600 bg-orange-50 border-orange-200",
  MEDIUM: "text-blue-600 bg-blue-50 border-blue-200",
  LOW: "text-gray-600 bg-gray-50 border-gray-200",
};

export default function MyRequests() {
  const [requests, setRequests] = useState<Ticket[]>([]);
  const [serviceDesks, setServiceDesks] = useState<any[]>([]);
  const [selectedDeskId, setSelectedDeskId] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedPriority, setSelectedPriority] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const ability = React.useContext(AbilityContext);

  const canSubmit = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_REQUESTER) || 
                    ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || 
                    ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [requestsRes, desksRes] = await Promise.all([
          serviceDeskApi.getMyRequests(),
          serviceDeskApi.getServiceDesks({ retired: false })
        ]);

        if (requestsRes.success) {
          const mappedTickets: Ticket[] = requestsRes.requests.map((req: any) => {
            const createdAt = new Date(req.createdAt);
            const now = new Date();
            const diffHours = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
            const slaHours = req.formVersion?.requestForm?.serviceDesk?.slaHours || 48;
            const isBreached = diffHours > slaHours && !['RESOLVED', 'CLOSED'].includes(req.status);

            const getInitials = (name: string) => name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || '?';
            const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-indigo-500'];
            const getUserColor = (userId: any) => colors[parseInt(userId) % colors.length] || 'bg-gray-500';

            return {
              id: req.id.toString(),
              formattedId: `REQ-${req.id.toString().padStart(3, '0')}`,
              title: req.title,
              category: req.formVersion?.requestForm?.name || "General Request",
              serviceDesk: req.formVersion?.requestForm?.serviceDesk?.isRetired 
                ? `${req.formVersion?.requestForm?.serviceDesk?.name} (retired)` 
                : (req.formVersion?.requestForm?.serviceDesk?.name || "Service Desk"),
              serviceDeskId: req.formVersion?.requestForm?.serviceDesk?.id?.toString(),
              status: req.status,
              priority: (req.priority.charAt(0).toUpperCase() + req.priority.slice(1).toLowerCase()) as any,
              submitter: { 
                name: req.requester?.name || "Unknown", 
                initials: getInitials(req.requester?.name),
                email: req.requester?.email || '',
                color: getUserColor(req.requester?.id)
              },
              assignees: (req.assignees || []).map((u: any) => ({
                name: u.name,
                initials: getInitials(u.name),
                email: u.email,
                color: getUserColor(u.id)
              })),
              createdAt: req.createdAt,
              isBreached,
              comments: (req.comments || []).filter((c: any) => !c.isPrivate).length,
              attachments: req.attachments?.length || 0,
              tags: req.data?.tags || []
            };
          });
          setRequests(mappedTickets);
        }

        if (desksRes.success) {
          setServiceDesks(desksRes.desks);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("An error occurred while loading your requests.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredRequests = requests.filter(req => {
    // Service Desk Filter
    if (selectedDeskId !== "all" && (req as any).serviceDeskId !== selectedDeskId) return false;
    
    // Status Filter
    if (selectedStatus !== "all" && req.status !== selectedStatus) return false;

    // Priority Filter
    if (selectedPriority !== "all" && req.priority !== selectedPriority) return false;

    // Search Query
    if (searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase();
        return (
            req.title.toLowerCase().includes(query) ||
            req.id.toLowerCase().includes(query) ||
            req.category.toLowerCase().includes(query)
        );
    }

    return true;
  });

  return (
    <div className="space-y-6 pb-10 md:pb-0">
      <div className="flex justify-between items-center sm:items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">My Requests</h1>
          <p className="text-sm text-muted-foreground">Manage your submitted service requests.</p>
        </div>
        {canSubmit && (
          <div className="flex items-center gap-2">
            {/* Desktop Action */}
            <Button 
              className="hidden sm:flex"
              onClick={() => navigate('/my-requests/new', { state: { from: '/my-requests' } })}
            >
                <PlusCircle className="mr-2 h-4 w-4" /> New Request
            </Button>
            
            {/* Mobile Action */}
            <Button 
              size="icon"
              className="sm:hidden"
              onClick={() => navigate('/my-requests/new', { state: { from: '/my-requests' } })}
            >
                <PlusCircle className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      {/* Stats - Grid on mobile, Flex on desktop */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
        <div className="bg-gray-100/50 rounded-lg p-3 border border-gray-100 shadow-sm sm:w-fit">
          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">Total Requests</p>
          <p className="text-2xl font-bold mt-0.5">{requests.length}</p>
        </div>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div key={key} className={cn("rounded-lg p-3 border shadow-sm sm:w-fit", config.color)}>
            <p className={cn("text-[10px] font-bold uppercase tracking-tight opacity-70")}>{config.label}</p>
            <p className={cn("text-2xl font-bold mt-0.5")}>{requests.filter(t => t.status === key).length}</p>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <TicketFilterBar 
        serviceDesks={serviceDesks}
        selectedDeskId={selectedDeskId}
        onDeskChange={setSelectedDeskId}
        statusConfig={STATUS_CONFIG}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        priorityConfig={PRIORITY_CONFIG}
        selectedPriority={selectedPriority}
        onPriorityChange={setSelectedPriority}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search my requests..."
      />

      <TicketListView 
        tickets={filteredRequests}
        statusConfig={STATUS_CONFIG}
        isLoading={isLoading}
        onTicketClick={(ticket) => navigate(`/service-desk/view/${ticket.id}`)}
        emptyState={
          <div className="flex flex-col items-center justify-center p-16 text-center bg-gray-50/30 rounded-xl border border-dashed border-gray-200">
            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <TicketIcon className="h-6 w-6 text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No requests found</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              {selectedDeskId !== "all" 
                ? "No requests found for this service desk."
                : "You haven't submitted any service requests yet."}
            </p>
            {canSubmit && (
                <Button className="mt-6 shadow-md" variant="outline" onClick={() => navigate('/my-requests/new', { state: { from: '/my-requests' } })}>
                    <PlusCircle className="mr-2 h-4 w-4" /> New Request
                </Button>
            )}
          </div>
        }
      />
    </div>
  );
}
